import React, { useState, useEffect } from 'react';
import { Download, Plus, Trash2, Eye, EyeOff, AlertCircle} from 'lucide-react';
import { downloadCoupaJSON, downloadCoupaCSV } from '../utils/CoupaExport';
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

const DirectEditField = ({ label, value, onChange, type = 'text', required = false, placeholder = '' }) => {
  const inputRef = React.useRef(null);
  
  return (
    <div className="mb-3">
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        ref={inputRef}
        type={type}
        defaultValue={value || ''}
        onBlur={(e) => onChange(e.target.value)}
        onChange={(e) => {
          inputRef.current.value = e.target.value;
        }}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
      />
    </div>
  );
};

const CoupaEditor = ({ invoiceData, setInvoiceData, pdfFile, showJson, setShowJson, user, exportFormat, setExportFormat }) => {
  const [expandedLines, setExpandedLines] = useState({});

  const updateField = (path, value) => {
    const newData = JSON.parse(JSON.stringify(invoiceData));
    const keys = path.split('.');
    let current = newData;
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    setInvoiceData(newData);
  };

const applyDefaultBilling = async () => {
  if (!user) return;
  
  try {
    // Read from userConfigs collection
    const configRef = doc(db, 'userConfigs', user.id);
    const configSnap = await getDoc(configRef);
    
    if (configSnap.exists()) {
      const config = configSnap.data();
      const newData = { ...invoiceData };
      
      // Apply all header-level configuration fields
      if (config.chartOfAccount) {
        newData['account-type'].name = config.chartOfAccount;
      }
      
      if (config.supplierNumber) {
        newData.supplier.number = config.supplierNumber;
      }
      
      if (config.shipToId) {
        newData['ship-to-address'].id = config.shipToId;
      }
      
      if (config.remitToCode) {
        newData['remit-to-address']['remit-to-code'] = config.remitToCode;
      }
      
      // Apply account code ONLY to lines missing PO Line Number (order-line-num)
      const accountCode = config.fullAccountCode || '';
      if (accountCode) {
        newData['invoice-lines'].forEach(line => {
          // Check if PO Line Number (order-line-num) is missing or empty
          if (!line['order-line-num'] || line['order-line-num'].trim() === '') {
            line.account = {
              code: accountCode,
              'account-type': { name: config.chartOfAccount || '' }
            };
          }
        });
      }
      
      setInvoiceData(newData);
      alert('✅ Default configuration applied: Supplier, Ship-To, Remit-To, Chart of Accounts, and account codes for non-PO lines!');
    } else {
      alert('⚠️ No saved configuration found. Please set it up in the Configuration tab first.');
    }
  } catch (error) {
    console.error('Error applying default billing:', error);
    alert('Failed to apply default billing');
  }
};
useEffect(() => {
  const loadDefaultConfig = async () => {
    if (!pdfFile || !user) return;
    
    const hasInvoiceNumber = invoiceData['invoice-number'];
    const hasNoConfig = !invoiceData['account-type'].name && !invoiceData.supplier.number;
    
    if (hasInvoiceNumber && hasNoConfig) {
      try {
        // Read from userConfigs collection
        const configRef = doc(db, 'userConfigs', user.id);
        const configSnap = await getDoc(configRef);
        
        if (configSnap.exists()) {
          const config = configSnap.data();
          const newData = { ...invoiceData };
          
          // Auto-populate all saved fields
          if (config.chartOfAccount) {
            newData['account-type'].name = config.chartOfAccount;
          }
          
          if (config.supplierNumber) {
            newData.supplier.number = config.supplierNumber;
          }
          
          if (config.shipToId) {
            newData['ship-to-address'].id = config.shipToId;
          }
          
          if (config.remitToCode) {
            newData['remit-to-address']['remit-to-code'] = config.remitToCode;
          }
          
          setInvoiceData(newData);
        }
      } catch (error) {
        console.error('Error loading default config:', error);
      }
    }
  };

  loadDefaultConfig();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [pdfFile, user]);

  const addInvoiceLine = () => {
    const newLine = {
      'line-num': String(invoiceData['invoice-lines'].length + 1),
      description: '',
      type: 'InvoiceQuantityLine',
      currency: { code: 'USD' },
      price: '',
      quantity: '',
      uom: { code: '' },
      'po-number': '',
      'order-line-num': '',
      'supplier-part-number': '',
      'auxiliary-part-number': '',
      account: {
        code: '',
        'account-type': { name: 'Chart of Accounts' }
      },
      'custom-fields': { 'invoice-line-total': '' },
      'tax-lines': []
    };
    setInvoiceData({
      ...invoiceData,
      'invoice-lines': [...invoiceData['invoice-lines'], newLine]
    });
  };

  const updateInvoiceLine = (index, field, value) => {
    const newLines = JSON.parse(JSON.stringify(invoiceData['invoice-lines']));
    
    if (!newLines[index].account) {
     newLines[index].account = {
        code: '',
        'account-type': { name: '' }
      };
    }
    
    const keys = field.split('.');
    let current = newLines[index];
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    setInvoiceData({ ...invoiceData, 'invoice-lines': newLines });
  };

  const removeLine = (index) => {
    const newLines = invoiceData['invoice-lines'].filter((_, i) => i !== index);
    setInvoiceData({ ...invoiceData, 'invoice-lines': newLines });
  };

  const addTaxLine = (lineIndex) => {
    const newLines = JSON.parse(JSON.stringify(invoiceData['invoice-lines']));
    if (!newLines[lineIndex]['tax-lines']) {
      newLines[lineIndex]['tax-lines'] = [];
    }
    newLines[lineIndex]['tax-lines'].push({
      amount: '',
      type: 'TaxLine',
      code: '',
      rate: '',
      base: ''
    });
    setInvoiceData({ ...invoiceData, 'invoice-lines': newLines });
  };

  const updateLineTax = (lineIndex, taxIndex, field, value) => {
    const newLines = JSON.parse(JSON.stringify(invoiceData['invoice-lines']));
    newLines[lineIndex]['tax-lines'][taxIndex][field] = value;
    setInvoiceData({ ...invoiceData, 'invoice-lines': newLines });
  };

  const removeLineTax = (lineIndex, taxIndex) => {
    const newLines = JSON.parse(JSON.stringify(invoiceData['invoice-lines']));
    newLines[lineIndex]['tax-lines'].splice(taxIndex, 1);
    setInvoiceData({ ...invoiceData, 'invoice-lines': newLines });
  };

  const addHeaderTax = () => {
    setInvoiceData({
      ...invoiceData,
      'tax-lines': [...(invoiceData['tax-lines'] || []), { amount: '', rate: '', base: '' }]
    });
  };

  const removeHeaderTax = (taxIndex) => {
    const updatedTaxes = invoiceData['tax-lines'].filter((_, i) => i !== taxIndex);
    setInvoiceData({ ...invoiceData, 'tax-lines': updatedTaxes });
  };

  const updateHeaderTax = (taxIndex, field, value) => {
    const updatedTaxes = [...invoiceData['tax-lines']];
    updatedTaxes[taxIndex] = { ...updatedTaxes[taxIndex], [field]: value };
    setInvoiceData({ ...invoiceData, 'tax-lines': updatedTaxes });
  };

  const toggleAllLines = () => {
    const allExpanded = Object.keys(expandedLines).length === invoiceData['invoice-lines'].length && 
                        Object.values(expandedLines).every(v => v);
    
    if (allExpanded) {
      setExpandedLines({});
    } else {
      const newExpanded = {};
      invoiceData['invoice-lines'].forEach((_, index) => {
        newExpanded[index] = true;
      });
      setExpandedLines(newExpanded);
    }
  };

  const toggleLine = (index) => {
    setExpandedLines({
      ...expandedLines,
      [index]: !expandedLines[index]
    });
  };

  return (
    <div className="space-y-4 h-[calc(100vh-120px)] overflow-y-auto pr-2">
      {/* Invoice Header */}
<div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5">
  <div className="flex items-center justify-between mb-4">
    <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2">
      <div className="w-1 h-5 bg-indigo-600 rounded"></div>
      Invoice Header
    </h4>
    <button
      onClick={applyDefaultBilling}
      className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg hover:shadow-lg transform hover:scale-105 transition-all text-sm font-medium"
    >
      <Download className="w-4 h-4" />
      Apply Default Configuration
    </button>
  </div>
  
  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg mb-4">
    <p className="text-xs text-emerald-800 font-medium">
      💡 Click "Apply Default Configuration" to auto-fill account codes for all non-PO lines from your saved settings
    </p>
  </div>
  
  <div className="grid grid-cols-2 gap-x-4">
    <DirectEditField label="Invoice Number" value={invoiceData['invoice-number']} onChange={(v) => updateField('invoice-number', v)} required placeholder="INV-12345" />
    <DirectEditField label="Invoice Date" value={invoiceData['invoice-date']} onChange={(v) => updateField('invoice-date', v)} required placeholder="MM/DD/YYYY" />
    <DirectEditField label="Due Date" value={invoiceData['due-date']} onChange={(v) => updateField('due-date', v)} placeholder="MM/DD/YYYY" />
    <DirectEditField label="Currency Code" value={invoiceData.currency.code} onChange={(v) => updateField('currency.code', v)} required placeholder="USD" />
    <DirectEditField label="Payment Terms" value={invoiceData['payment-terms']} onChange={(v) => updateField('payment-terms', v)} placeholder="Net 30" />
    <DirectEditField label="Requester Email" value={invoiceData['requester-email']} onChange={(v) => updateField('requester-email', v)} type="email" placeholder="user@company.com" />
    <div className="col-span-2">
      <DirectEditField 
        label="Chart of Accounts (Account Type)" 
        value={invoiceData['account-type'].name} 
        onChange={(v) => updateField('account-type.name', v)} 
        required 
        placeholder="Will auto-populate from saved config" 
      />
    </div>
  </div>
</div>

      {/* Supplier Information */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5">
        <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-1 h-5 bg-purple-600 rounded"></div>
          Supplier Information
        </h4>
        <div className="grid grid-cols-2 gap-x-4">
          <DirectEditField label="Supplier Number" value={invoiceData.supplier.number} onChange={(v) => updateField('supplier.number', v)} required placeholder="SUP-001" />
          <DirectEditField label="Supplier Site Code" value={invoiceData['supplier-site'].code} onChange={(v) => updateField('supplier-site.code', v)} placeholder="SITE-01" />
          <DirectEditField label="Remit To Code" value={invoiceData['remit-to-address']['remit-to-code']} onChange={(v) => updateField('remit-to-address.remit-to-code', v)} placeholder="REM-001" />
          <DirectEditField label="Ship To ID" value={invoiceData['ship-to-address'].id} onChange={(v) => updateField('ship-to-address.id', v)} placeholder="SHIP-001" />
        </div>
      </div>
      {/* Invoice Charges */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5">
        <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-1 h-5 bg-cyan-600 rounded"></div>
          Invoice Charges
        </h4>
        <div className="grid grid-cols-2 gap-x-4">
          <DirectEditField 
            label="Handling Amount" 
            value={invoiceData['handling-amount']} 
            onChange={(v) => updateField('handling-amount', v)} 
            type="number"
            placeholder="0.00" 
          />
          <DirectEditField 
            label="Shipping Amount" 
            value={invoiceData['shipping-amount']} 
            onChange={(v) => updateField('shipping-amount', v)} 
            type="number"
            placeholder="0.00" 
          />
          <DirectEditField 
            label="Miscellaneous Amount" 
            value={invoiceData['misc-amount']} 
            onChange={(v) => updateField('misc-amount', v)} 
            type="number"
            placeholder="0.00" 
          />
          <DirectEditField 
            label="Discount Amount" 
            value={invoiceData['discount-amount']} 
            onChange={(v) => updateField('discount-amount', v)} 
            type="number"
            placeholder="0.00" 
          />
        </div>
      </div>

      {/* Invoice Line Items */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <div className="w-1 h-5 bg-pink-600 rounded"></div>
            Invoice Line Items ({invoiceData['invoice-lines'].length})
          </h4>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleAllLines}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
            >
              {Object.keys(expandedLines).length === invoiceData['invoice-lines'].length && 
               Object.values(expandedLines).every(v => v) ? (
                <>
                  <EyeOff className="w-4 h-4" />
                  Collapse All
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  Expand All
                </>
              )}
            </button>
            <button
              onClick={addInvoiceLine}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Add Line
            </button>
          </div>
        </div>

        {invoiceData['invoice-lines'].map((line, index) => (
          <div key={index} className="mb-4 p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleLine(index)}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                >
                  {expandedLines[index] ? (
                    <EyeOff className="w-4 h-4 text-gray-600" />
                  ) : (
                    <Eye className="w-4 h-4 text-gray-600" />
                  )}
                </button>
                <span className="text-sm font-semibold text-indigo-600">Line {line['line-num']}</span>
                {!line['order-line-num'] && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">Non-PO</span>
                )}
                <span className="text-xs text-gray-500">
                  {line.description ? `- ${line.description.substring(0, 30)}${line.description.length > 30 ? '...' : ''}` : ''}
                </span>
              </div>
              <button onClick={() => removeLine(index)} className="p-1 text-red-600 hover:bg-red-50 rounded-lg">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            {expandedLines[index] && (
              <>
                <div className="grid grid-cols-2 gap-x-3">
                  <div className="col-span-2">
                    <DirectEditField label="Description" value={line.description} onChange={(v) => updateInvoiceLine(index, 'description', v)} placeholder="Item description" />
                  </div>
                  <DirectEditField label="PO Number" value={line['po-number']} onChange={(v) => updateInvoiceLine(index, 'po-number', v)} placeholder="PO-12345" />
                  <DirectEditField label="PO Line Number" value={line['order-line-num']} onChange={(v) => updateInvoiceLine(index, 'order-line-num', v)} placeholder="1" />
                  <DirectEditField label="Quantity" value={line.quantity} onChange={(v) => updateInvoiceLine(index, 'quantity', v)} type="number" placeholder="1" />
                  <DirectEditField label="Unit Price" value={line.price} onChange={(v) => updateInvoiceLine(index, 'price', v)} type="number" placeholder="100.00" />
                  <DirectEditField label="UOM Code" value={line.uom.code} onChange={(v) => updateInvoiceLine(index, 'uom.code', v)} placeholder="EA" />
                  <DirectEditField label="Line Total" value={line['custom-fields']['invoice-line-total']} onChange={(v) => updateInvoiceLine(index, 'custom-fields.invoice-line-total', v)} type="number" placeholder="100.00" />
                </div>

                {!line['order-line-num'] && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-800 mb-2 font-medium flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Non-PO Invoice - GL Account Coding Required
                    </p>
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Full Account Code (20 Segments)
                      </label>
                      <input
                        type="text"
                        value={line.account?.code || ''}
                        disabled
                        className="w-full px-3 py-2 text-xs font-mono border border-gray-300 rounded-lg bg-gray-100 text-gray-700"
                      />
                      <p className="text-xs text-gray-500 mt-1">Applied from Default Account Code above</p>
                    </div>
                  </div>
                )}

                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-blue-800 font-medium">Tax Lines</p>
                    <button
                      onClick={() => addTaxLine(index)}
                      className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      + Add Tax
                    </button>
                  </div>
                  {line['tax-lines'] && line['tax-lines'].map((tax, taxIndex) => (
                    <div key={taxIndex} className="mb-2 p-2 bg-white rounded border border-blue-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-blue-700">Tax {taxIndex + 1}</span>
                        <button
                          onClick={() => removeLineTax(index, taxIndex)}
                          className="p-0.5 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <DirectEditField label="Tax Amount" value={tax.amount} onChange={(v) => updateLineTax(index, taxIndex, 'amount', v)} type="number" placeholder="10.00" />
                        <DirectEditField label="Tax Rate %" value={tax.rate} onChange={(v) => updateLineTax(index, taxIndex, 'rate', v)} type="number" placeholder="8.5" />
                        <DirectEditField label="Tax Base" value={tax.base} onChange={(v) => updateLineTax(index, taxIndex, 'base', v)} type="number" placeholder="100.00" />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Header Level Taxes */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700">Header Taxes</h3>
          <button
            onClick={addHeaderTax}
            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            + Add Header Tax
          </button>
        </div>
        
        {invoiceData['tax-lines'] && invoiceData['tax-lines'].map((tax, taxIndex) => (
          <div key={taxIndex} className="mb-2 p-2 bg-white rounded border border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-blue-700">Tax {taxIndex + 1}</span>
              <button
                onClick={() => removeHeaderTax(taxIndex)}
                className="p-0.5 text-red-600 hover:bg-red-50 rounded"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <DirectEditField 
                label="Tax Amount" 
                value={tax.amount} 
                onChange={(v) => updateHeaderTax(taxIndex, 'amount', v)} 
                type="number" 
                placeholder="10.00" 
              />
              <DirectEditField 
                label="Tax Rate %" 
                value={tax.rate} 
                onChange={(v) => updateHeaderTax(taxIndex, 'rate', v)} 
                type="number" 
                placeholder="8.5" 
              />
              <DirectEditField 
                label="Tax Base" 
                value={tax.base} 
                onChange={(v) => updateHeaderTax(taxIndex, 'base', v)} 
                type="number" 
                placeholder="100.00" 
              />
            </div>
          </div>
        ))}
      </div>

      {/* Invoice Summary */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5">
        <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-1 h-5 bg-green-600 rounded"></div>
          Invoice Summary
        </h4>
        <div className="space-y-3">
          <DirectEditField label="Invoice Total" value={invoiceData['custom-fields']['invoice-total']} onChange={(v) => updateField('custom-fields.invoice-total', v)} type="number" placeholder="1000.00" />
          <div className="flex justify-between items-center pt-3 border-t border-gray-200">
            <span className="text-lg font-semibold text-gray-900">Total Amount:</span>
            <span className="text-2xl font-bold text-indigo-600">
              {invoiceData.currency.code} {invoiceData['custom-fields']['invoice-total']}
            </span>
          </div>
        </div>
      </div>

      {/* JSON Preview */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5">
        <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-1 h-5 bg-orange-600 rounded"></div>
          JSON Preview (View Only)
        </h4>
        <button
          onClick={() => setShowJson(!showJson)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium mb-3"
        >
          {showJson ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          {showJson ? 'Hide' : 'Show'} JSON Structure
        </button>
        {showJson && (
          <div className="relative">
            <div className="absolute top-2 right-2 z-10 px-2 py-1 bg-red-500 text-white text-xs rounded font-medium">
              READ ONLY
            </div>
            <pre
              className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-auto max-h-[500px]"
              style={{
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none'
              }}
            >
              {(() => {
                const cleanData = { ...invoiceData };
                delete cleanData['default-account-code'];
                return JSON.stringify(cleanData, null, 2);
              })()}
            </pre>
          </div>
        )}
      </div>

     ={/* Export Options */}
<div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5">
  <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
    <div className="w-1 h-5 bg-indigo-600 rounded"></div>
    Export Coupa Data
  </h4>
  
  <div className="space-y-3">
    {/* Export Format Selector */}
    <div className="flex items-center gap-2">
      <label className="text-xs font-medium text-gray-700 whitespace-nowrap">
        Format:
      </label>
      <select
        value={exportFormat}
        onChange={(e) => setExportFormat(e.target.value)}
        className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
      >
        <option value="csv">CSV (Recommended)</option>
        <option value="json">JSON (API Integration)</option>
      </select>
    </div>

    {/* Download Buttons */}
    {exportFormat === 'json' && (
      <button
        onClick={() => downloadCoupaJSON(invoiceData)}
        className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transform hover:scale-105 transition-all"
      >
        <Download className="w-5 h-5" />
        Download Coupa JSON
      </button>
    )}

    {exportFormat === 'csv' && (
      <button
        onClick={() => downloadCoupaCSV(invoiceData, pdfFile)}
        className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium hover:shadow-lg transform hover:scale-105 transition-all"
      >
        <Download className="w-5 h-5" />
        Download Coupa CSV
      </button>
    )}
  </div>

  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
    {exportFormat === 'json' ? (
      <div>
        <p className="text-xs font-semibold text-blue-900 mb-1">
          🔌 Coupa API Integration
        </p>
        <p className="text-xs text-blue-800">
          Use this JSON for direct API integration with Coupa's invoice endpoint.
        </p>
      </div>
    ) : (
      <div>
        <p className="text-xs font-semibold text-blue-900 mb-1">
          📁 Coupa Manual Import
        </p>
        <p className="text-xs text-blue-800">
          Use this CSV for manual file upload via Coupa's "Load From File" feature.
        </p>
      </div>
    )}
  </div>

  <p className="text-xs text-gray-500 text-center mt-3">
    ✅ No payment required - Download freely after processing
  </p>
</div>
    </div>
  );
};

export default CoupaEditor;